import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  DocumentArrowUpIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';
import ConfirmationModal from './ConfirmationModal';
import DocumentViewer from './DocumentViewer';
import { ModalActions, ModalSection, ModalShell } from './modals/core';

interface BankStatement {
  id: number;
  document_id: number;
  document_name: string;
  document_size?: number | null;
  zeitraum_von: string;
  zeitraum_bis: string;
  auszugsnummer?: string | null;
  bank_name?: string | null;
  kontoinhaber?: string | null;
  iban_ende?: string | null;
  anfangssaldo?: number | null;
  endsaldo?: number | null;
  waehrung: string;
  anzahl_buchungen?: number | null;
  notiz?: string | null;
  ki_analysiert: number;
  system_endsaldo?: number | null;
  saldo_abweichung?: number | null;
}

interface UploadEntry {
  dateiname: string;
  zeitraum_von: string;
  zeitraum_bis: string;
  auszugsnummer: string;
  bank_name: string;
  kontoinhaber: string;
  iban_ende: string;
  anfangssaldo: string;
  endsaldo: string;
  waehrung: string;
  anzahl_buchungen: string;
  notiz: string;
  ki_analysiert: boolean;
  ki_confidence?: Record<string, number | null>;
  analyseFehler?: string;
}

interface Props {
  accountId: number;
  accountName: string;
  currency?: string;
  onCountChange?: (count: number) => void;
}

const leererEintrag = (waehrung = 'EUR'): UploadEntry => ({
  dateiname: '',
  zeitraum_von: '',
  zeitraum_bis: '',
  auszugsnummer: '',
  bank_name: '',
  kontoinhaber: '',
  iban_ende: '',
  anfangssaldo: '',
  endsaldo: '',
  waehrung,
  anzahl_buchungen: '',
  notiz: '',
  ki_analysiert: false,
});

const periodFromFilename = (name: string, currency = 'EUR'): UploadEntry => {
  const entry = leererEintrag(currency);
  entry.dateiname = name;
  const match = name.match(/(?:^|\D)(20\d{2})[-_. ](0?[1-9]|1[0-2])(?:\D|$)/);
  if (!match) return entry;
  const year = Number(match[1]);
  const month = Number(match[2]);
  entry.zeitraum_von = `${year}-${String(month).padStart(2, '0')}-01`;
  entry.zeitraum_bis = `${year}-${String(month).padStart(2, '0')}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;
  return entry;
};

const kurzerDateiname = (start?: string, end?: string) => {
  if (!start || !end) return '';
  const startDate = new Date(`${start.slice(0, 10)}T12:00:00`);
  const endDate = new Date(`${end.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return '';

  const month = (date: Date) => new Intl.DateTimeFormat('de-DE', { month: 'long' }).format(date);
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  if (startYear === endYear && startDate.getMonth() === endDate.getMonth()) {
    return `${month(startDate)} ${startYear}.pdf`;
  }
  if (startYear === endYear) {
    return `${month(startDate)}–${month(endDate)} ${startYear}.pdf`;
  }
  return `${month(startDate)} ${startYear}–${month(endDate)} ${endYear}.pdf`;
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('de-DE');
};

const formatMoney = (value: number | string | null | undefined, currency = 'EUR') => {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(amount);
};

const formatSize = (bytes?: number | null) => {
  if (!bytes) return '';
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
};

const BankStatementManager: React.FC<Props> = ({ accountId, accountName, currency = 'EUR', onCountChange }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [statements, setStatements] = useState<BankStatement[]>([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [entries, setEntries] = useState<UploadEntry[]>([]);
  const [useAi, setUseAi] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewStatement, setPreviewStatement] = useState<BankStatement | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BankStatement | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadStatements = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/api/finance/accounts/${accountId}/bank-statements`);
      const nextStatements = response.data?.statements || [];
      setStatements(nextStatements);
      onCountChange?.(nextStatements.length);
    } catch (error: any) {
      setNotice({ type: 'error', text: error.response?.data?.error || 'Kontoauszüge konnten nicht geladen werden.' });
    } finally {
      setLoading(false);
    }
  }, [accountId, onCountChange]);

  useEffect(() => { loadStatements(); }, [loadStatements]);

  const resetUpload = () => {
    setFiles([]);
    setEntries([]);
    setUseAi(false);
    setAnalyzing(false);
    setSaving(false);
  };

  const closeUpload = () => {
    if (analyzing || saving) return;
    setUploadOpen(false);
    resetUpload();
  };

  const addFiles = (incoming: File[]) => {
    const pdfs = incoming.filter((file) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'));
    const accepted = pdfs.filter((file) => file.size <= 20 * 1024 * 1024);
    const room = Math.max(0, 20 - files.length);
    const next = accepted.slice(0, room);
    if (!next.length) {
      setNotice({ type: 'error', text: 'Bitte PDF-Dateien bis maximal 20 MB auswählen.' });
      return;
    }
    setFiles((previous) => [...previous, ...next]);
    setEntries((previous) => [...previous, ...next.map((file) => periodFromFilename(file.name, currency))]);
    if (incoming.length !== next.length) {
      setNotice({ type: 'info', text: 'Es werden höchstens 20 PDFs bis jeweils 20 MB übernommen.' });
    }
  };

  const removeFile = (index: number) => {
    setFiles((previous) => previous.filter((_file, current) => current !== index));
    setEntries((previous) => previous.filter((_entry, current) => current !== index));
  };

  const patchEntry = (index: number, patch: Partial<UploadEntry>) => {
    setEntries((previous) => previous.map((entry, current) => current === index ? { ...entry, ...patch } : entry));
  };

  const analyzeFiles = async () => {
    if (!files.length) return;
    setAnalyzing(true);
    setNotice(null);
    try {
      const form = new FormData();
      files.forEach((file) => form.append('documents', file));
      const response = await apiClient.post('/api/finance/bank-statements/analyze', form, { timeout: 180000 });
      const results = response.data?.results || [];
      setEntries((previous) => previous.map((entry, index) => {
        const result = results.find((item: any) => item.index === index);
        if (!result?.success) return { ...entry, analyseFehler: result?.error || 'Keine Daten erkannt', ki_analysiert: false };
        const data = result.data || {};
        const periodStart = data.period_start || entry.zeitraum_von;
        const periodEnd = data.period_end || entry.zeitraum_bis;
        return {
          ...entry,
          dateiname: kurzerDateiname(periodStart, periodEnd) || entry.dateiname,
          zeitraum_von: periodStart,
          zeitraum_bis: periodEnd,
          auszugsnummer: data.statement_number || entry.auszugsnummer,
          bank_name: data.bank_name || entry.bank_name,
          kontoinhaber: data.account_holder || entry.kontoinhaber,
          iban_ende: data.iban_suffix || entry.iban_ende,
          anfangssaldo: data.opening_balance ?? entry.anfangssaldo,
          endsaldo: data.closing_balance ?? entry.endsaldo,
          waehrung: data.currency || entry.waehrung,
          anzahl_buchungen: data.transaction_count ?? entry.anzahl_buchungen,
          notiz: data.note || entry.notiz,
          ki_analysiert: true,
          ki_confidence: data.confidence,
          analyseFehler: undefined,
        };
      }));
      const ok = results.filter((result: any) => result.success).length;
      const failed = results.length - ok;
      setNotice({
        type: failed ? 'info' : 'success',
        text: failed ? `${ok} PDF(s) ausgelesen, ${failed} bitte manuell ergänzen.` : `Alle ${ok} PDF(s) wurden ausgelesen. Bitte Werte kurz prüfen.`,
      });
    } catch (error: any) {
      setNotice({ type: 'error', text: error.response?.data?.error || 'KI-Analyse konnte nicht abgeschlossen werden.' });
    } finally {
      setAnalyzing(false);
    }
  };

  const saveFiles = async () => {
    const invalidIndex = entries.findIndex((entry) => !entry.zeitraum_von || !entry.zeitraum_bis || entry.zeitraum_bis < entry.zeitraum_von);
    if (invalidIndex >= 0) {
      setNotice({ type: 'error', text: `Bitte den gültigen Zeitraum für „${files[invalidIndex].name}“ ergänzen.` });
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      const form = new FormData();
      files.forEach((file) => form.append('documents', file));
      form.append('entries', JSON.stringify(entries));
      const response = await apiClient.post(`/api/finance/accounts/${accountId}/bank-statements`, form, { timeout: 180000 });
      const results = response.data?.results || [];
      const stored = results.filter((result: any) => result.success && !result.duplicate).length;
      const duplicates = results.filter((result: any) => result.duplicate).length;
      const failed = results.filter((result: any) => !result.success).length;
      await loadStatements();
      setUploadOpen(false);
      resetUpload();
      setNotice({
        type: failed ? 'info' : 'success',
        text: `${stored} Kontoauszug/auszüge gespeichert${duplicates ? `, ${duplicates} Duplikat(e) übersprungen` : ''}${failed ? `, ${failed} fehlgeschlagen` : ''}.`,
      });
    } catch (error: any) {
      setNotice({ type: 'error', text: error.response?.data?.error || 'Kontoauszüge konnten nicht gespeichert werden.' });
      setSaving(false);
    }
  };

  const download = async (statement: BankStatement) => {
    try {
      const response = await apiClient.get(`/api/documents/download/${statement.document_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = statement.document_name || 'Kontoauszug.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error: any) {
      setNotice({ type: 'error', text: error.response?.data?.error || 'PDF konnte nicht geöffnet werden.' });
    }
  };

  const deleteStatement = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/api/finance/bank-statements/${deleteTarget.id}`);
      setDeleteTarget(null);
      await loadStatements();
      setNotice({ type: 'success', text: 'Kontoauszug wurde gelöscht.' });
    } catch (error: any) {
      setNotice({ type: 'error', text: error.response?.data?.error || 'Kontoauszug konnte nicht gelöscht werden.' });
    } finally {
      setDeleting(false);
    }
  };

  const allPeriodsValid = files.length > 0 && entries.every((entry) => entry.zeitraum_von && entry.zeitraum_bis && entry.zeitraum_bis >= entry.zeitraum_von);

  return (
    <>
      <ModalSection className="mb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-info-50 text-info-600 dark:bg-info-900/25 dark:text-info-400">
              <DocumentTextIcon className="h-[18px] w-[18px]" />
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Kontoauszüge (PDF)</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {statements.length ? `${statements.length} verschlüsselt abgelegt` : 'Originalnachweise direkt am Konto'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { setUploadOpen(true); setNotice(null); }}
            className="inline-flex h-9 flex-none items-center gap-1.5 rounded-lg bg-primary-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-primary-700"
          >
            <PlusIcon className="h-4 w-4" /> PDFs hinzufügen
          </button>
        </div>

        {notice && (
          <div className={`mt-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${
            notice.type === 'error'
              ? 'border-error-200 bg-error-50 text-error-700 dark:border-error-800 dark:bg-error-900/20 dark:text-error-300'
              : notice.type === 'success'
                ? 'border-success-200 bg-success-50 text-success-700 dark:border-success-800 dark:bg-success-900/20 dark:text-success-300'
                : 'border-info-200 bg-info-50 text-info-700 dark:border-info-800 dark:bg-info-900/20 dark:text-info-300'
          }`}>
            {notice.type === 'error' ? <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-none" /> : <CheckCircleIcon className="mt-0.5 h-4 w-4 flex-none" />}
            <span>{notice.text}</span>
          </div>
        )}

        {loading ? (
          <div className="mt-4 space-y-2">
            {[1, 2].map((item) => <div key={item} className="h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-700/50" />)}
          </div>
        ) : statements.length ? (
          <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
            {statements.map((statement, index) => {
              const diff = statement.saldo_abweichung === null || statement.saldo_abweichung === undefined
                ? null : Number(statement.saldo_abweichung);
              const balanced = diff !== null && Math.abs(diff) < 0.01;
              return (
                <div key={statement.id} className={`flex items-center gap-3 px-3 py-3 ${index ? 'border-t border-gray-200 dark:border-gray-700' : ''}`}>
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                    <DocumentTextIcon className="h-[18px] w-[18px]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                        {formatDate(statement.zeitraum_von)} – {formatDate(statement.zeitraum_bis)}
                      </p>
                      {statement.ki_analysiert ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                          <SparklesIcon className="h-3 w-3" /> KI ausgelesen
                        </span>
                      ) : null}
                      {balanced ? (
                        <span className="rounded-full bg-success-50 px-2 py-0.5 text-[10px] font-semibold text-success-700 dark:bg-success-900/25 dark:text-success-300">Saldo stimmt</span>
                      ) : diff !== null ? (
                        <span className="rounded-full bg-warning-50 px-2 py-0.5 text-[10px] font-semibold text-warning-700 dark:bg-warning-900/25 dark:text-warning-300">Abweichung {formatMoney(diff, statement.waehrung)}</span>
                      ) : null}
                    </div>
                    <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                      {[statement.bank_name, statement.auszugsnummer ? `Auszug ${statement.auszugsnummer}` : null, formatSize(statement.document_size)].filter(Boolean).join(' · ') || statement.document_name}
                    </p>
                    {statement.endsaldo !== null && statement.endsaldo !== undefined && (
                      <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">Endsaldo laut Bank: {formatMoney(statement.endsaldo, statement.waehrung)}</p>
                    )}
                  </div>
                  <div className="flex flex-none items-center gap-1">
                    <button type="button" onClick={() => setPreviewStatement(statement)} title="PDF ansehen" className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"><EyeIcon className="h-4 w-4" /></button>
                    <button type="button" onClick={() => download(statement)} title="PDF herunterladen" className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"><ArrowDownTrayIcon className="h-4 w-4" /></button>
                    <button type="button" onClick={() => setDeleteTarget(statement)} title="Kontoauszug löschen" className="flex h-8 w-8 items-center justify-center rounded-lg text-error-500 hover:bg-error-50 dark:hover:bg-error-900/20"><TrashIcon className="h-4 w-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 px-4 py-5 text-sm font-semibold text-primary-600 transition-colors hover:bg-primary-50 dark:border-gray-600 dark:text-primary-400 dark:hover:bg-primary-900/20"
          >
            <DocumentArrowUpIcon className="h-5 w-5" /> Erste Kontoauszüge ablegen
          </button>
        )}
      </ModalSection>

      <ModalShell
        open={uploadOpen}
        onClose={closeUpload}
        variant="form"
        size="xl"
        icon={DocumentArrowUpIcon}
        title="Kontoauszüge hinzufügen"
        subtitle={`${accountName} · einzelne oder mehrere PDFs`}
        closeOnBackdrop={!analyzing && !saving}
        footer={
          <ModalActions
            secondary={{ label: 'Abbrechen', onClick: closeUpload, disabled: analyzing || saving }}
            primary={{
              label: files.length > 1 ? `${files.length} PDFs speichern` : 'PDF speichern',
              onClick: saveFiles,
              disabled: !allPeriodsValid || analyzing,
              loading: saving,
              icon: <DocumentArrowUpIcon className="h-4 w-4" />,
            }}
          />
        }
      >
        <div className="space-y-4">
          <div
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => { event.preventDefault(); addFiles(Array.from(event.dataTransfer.files)); }}
            className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-5 py-7 text-center dark:border-gray-600 dark:bg-gray-900/30"
          >
            <input ref={inputRef} type="file" accept="application/pdf,.pdf" multiple className="hidden" onChange={(event) => { addFiles(Array.from(event.target.files || [])); event.target.value = ''; }} />
            <DocumentArrowUpIcon className="mx-auto h-8 w-8 text-primary-500" />
            <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">PDFs hier ablegen oder auswählen</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Bis zu 20 Dateien, jeweils maximal 20 MB</p>
            <button type="button" onClick={() => inputRef.current?.click()} className="mt-3 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700">Dateien auswählen</button>
          </div>

          {files.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-[#1c2432]">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <label className="flex cursor-pointer items-start gap-2.5">
                  <input type="checkbox" checked={useAi} onChange={(event) => setUseAi(event.target.checked)} disabled={analyzing || saving} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span>
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-white"><SparklesIcon className="h-4 w-4 text-primary-500" /> KI-Assistent verwenden</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">Nur nach Ihrem Klick werden die PDFs zur KI-Analyse übertragen.</span>
                  </span>
                </label>
                {useAi && (
                  <button type="button" onClick={analyzeFiles} disabled={analyzing || saving} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-50">
                    <SparklesIcon className={`h-4 w-4 ${analyzing ? 'animate-pulse' : ''}`} />
                    {analyzing ? `${files.length} PDF(s) werden gelesen …` : 'Alle PDFs auslesen'}
                  </button>
                )}
              </div>
            </div>
          )}

          {notice && uploadOpen && (
            <div className={`flex items-start gap-2 rounded-xl border px-3 py-2.5 text-xs ${notice.type === 'error' ? 'border-error-200 bg-error-50 text-error-700 dark:border-error-800 dark:bg-error-900/20 dark:text-error-300' : 'border-info-200 bg-info-50 text-info-700 dark:border-info-800 dark:bg-info-900/20 dark:text-info-300'}`}>
              {notice.type === 'error' ? <ExclamationTriangleIcon className="h-4 w-4 flex-none" /> : <SparklesIcon className="h-4 w-4 flex-none" />}
              {notice.text}
            </div>
          )}

          <div className="space-y-3">
            {files.map((file, index) => {
              const entry = entries[index] || leererEintrag(currency);
              return (
                <div key={`${file.name}-${file.lastModified}-${index}`} className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432]">
                  <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                    <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-error-50 text-error-600 dark:bg-error-900/20 dark:text-error-400"><DocumentTextIcon className="h-4 w-4" /></span>
                    <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{file.name}</p><p className="text-[11px] text-gray-400">{formatSize(file.size)}</p></div>
                    {entry.ki_analysiert && <span className="hidden items-center gap-1 rounded-full bg-primary-50 px-2 py-1 text-[10px] font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 sm:inline-flex"><SparklesIcon className="h-3 w-3" /> ausgelesen</span>}
                    <button type="button" onClick={() => removeFile(index)} disabled={analyzing || saving} aria-label={`${file.name} entfernen`} className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"><XMarkIcon className="h-4 w-4" /></button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
                    <label className="text-xs font-medium text-gray-600 sm:col-span-2 lg:col-span-4 dark:text-gray-300">
                      <span className="flex items-center gap-2">
                        Dateiname
                        {entry.ki_analysiert && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                            <SparklesIcon className="h-3 w-3" /> KI-Vorschlag
                          </span>
                        )}
                      </span>
                      <input
                        type="text"
                        maxLength={255}
                        value={entry.dateiname}
                        onChange={(event) => patchEntry(index, { dateiname: event.target.value })}
                        placeholder="z. B. Januar 2025.pdf"
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                      />
                    </label>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Zeitraum von *<input type="date" value={entry.zeitraum_von} onChange={(event) => patchEntry(index, { zeitraum_von: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white" /></label>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Zeitraum bis *<input type="date" value={entry.zeitraum_bis} onChange={(event) => patchEntry(index, { zeitraum_bis: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white" /></label>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Anfangssaldo<input type="number" step="0.01" value={entry.anfangssaldo} onChange={(event) => patchEntry(index, { anfangssaldo: event.target.value })} placeholder="optional" className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white" /></label>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Endsaldo<input type="number" step="0.01" value={entry.endsaldo} onChange={(event) => patchEntry(index, { endsaldo: event.target.value })} placeholder="optional" className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white" /></label>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300 lg:col-span-2">Bank<input type="text" value={entry.bank_name} onChange={(event) => patchEntry(index, { bank_name: event.target.value })} placeholder="optional" className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white" /></label>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Auszugsnummer<input type="text" value={entry.auszugsnummer} onChange={(event) => patchEntry(index, { auszugsnummer: event.target.value })} placeholder="optional" className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white" /></label>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300">IBAN-Ende<input type="text" maxLength={4} value={entry.iban_ende} onChange={(event) => patchEntry(index, { iban_ende: event.target.value.replace(/\s/g, '').slice(-4) })} placeholder="letzte 4 Zeichen" className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white" /></label>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300 lg:col-span-2">Kontoinhaber<input type="text" value={entry.kontoinhaber} onChange={(event) => patchEntry(index, { kontoinhaber: event.target.value })} placeholder="optional" className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white" /></label>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Anzahl Buchungen<input type="number" min="0" step="1" value={entry.anzahl_buchungen} onChange={(event) => patchEntry(index, { anzahl_buchungen: event.target.value })} placeholder="optional" className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white" /></label>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Währung<input type="text" maxLength={10} value={entry.waehrung} onChange={(event) => patchEntry(index, { waehrung: event.target.value.toUpperCase() })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white" /></label>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300 sm:col-span-2 lg:col-span-4">Notiz<textarea rows={2} value={entry.notiz} onChange={(event) => patchEntry(index, { notiz: event.target.value })} placeholder="optional" className="mt-1 w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white" /></label>
                    {entry.analyseFehler && <p className="sm:col-span-2 lg:col-span-4 flex items-center gap-1.5 text-xs text-warning-700 dark:text-warning-300"><ExclamationTriangleIcon className="h-4 w-4" />{entry.analyseFehler}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </ModalShell>

      {previewStatement && (
        <DocumentViewer
          documentId={previewStatement.document_id}
          fileName={previewStatement.document_name || 'Kontoauszug.pdf'}
          filePath={previewStatement.document_name || 'Kontoauszug.pdf'}
          onClose={() => setPreviewStatement(null)}
          readOnly
          variant="modal"
          pdfInitialScale="page-width"
        />
      )}

      <ConfirmationModal
        visible={Boolean(deleteTarget)}
        type="warning"
        destructive
        variant="two"
        title="Kontoauszug löschen?"
        message={deleteTarget ? `Die PDF „${deleteTarget.document_name}“ und ihre Kontozuordnung werden dauerhaft gelöscht.` : ''}
        onClose={() => setDeleteTarget(null)}
        buttons={{
          cancel: { text: 'Abbrechen', onPress: () => setDeleteTarget(null) },
          confirm: { text: 'Löschen', onPress: deleteStatement, loading: deleting },
        }}
      />
    </>
  );
};

export default BankStatementManager;
